import React, { useEffect, useState, useRef } from 'react';
import { ShieldCheck, ArrowLeft, CheckCircle, RefreshCw, Lock } from 'lucide-react';
import { API_BASE_URL } from '../config';
import './OtpVerification.css';

function VerifyEmail() {
  const [email, setEmail]   = useState('');
  const [intent, setIntent] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [otp, setOtp]       = useState(['', '', '', '', '', '']);
  const [status, setStatus] = useState('idle'); // idle, verifying, success, error
  const [message, setMessage] = useState('');

  // Resend OTP Cooldown
  const [cooldown, setCooldown]   = useState(60);
  const [canResend, setCanResend] = useState(false);

  const inputsRef = useRef([]);

  useEffect(() => {
    const params    = new URLSearchParams(window.location.hash.split('?')[1]);
    const urlEmail  = params.get('email');
    const urlIntent = params.get('intent') || 'verify';
    const remember  = params.get('remember') === '1';

    if (urlEmail) setEmail(urlEmail);
    setIntent(urlIntent);
    setRememberDevice(remember);

    if (!urlEmail) {
      setStatus('error');
      setMessage('Missing email address. Please restart the process.');
    }
  }, []);

  useEffect(() => {
    let timer;
    if (cooldown > 0 && !canResend) {
      timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    } else if (cooldown === 0) {
      setCanResend(true);
    }
    return () => clearInterval(timer);
  }, [cooldown, canResend]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) inputsRef.current[index + 1].focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6).replace(/\D/g, '');
    if (pastedData) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedData.length; i++) newOtp[i] = pastedData[i];
      setOtp(newOtp);
      const focusIndex = pastedData.length < 6 ? pastedData.length : 5;
      inputsRef.current[focusIndex].focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = otp.join('');
    if (token.length !== 6) {
      setStatus('error');
      setMessage('Please enter all 6 digits.');
      return;
    }

    setStatus('verifying');
    setMessage('');

    try {
      const isLoginIntent = intent === 'login' || intent.startsWith('login');
      const endpoint = isLoginIntent ? '/api/auth/verify-login-otp' : '/api/auth/verify-email';

      const body = { email, token };
      if (isLoginIntent) body.rememberDevice = rememberDevice;

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();

      if (response.ok && data.success) {
        setStatus('success');
        setMessage(data.message);

        if (isLoginIntent && data.token) {
          localStorage.setItem('dms_auth_token', data.token);

          // Persist trusted device token if the backend issued one
          if (data.trustedDeviceToken) {
            localStorage.setItem('dms_trusted_device_token', data.trustedDeviceToken);
            localStorage.setItem('dms_trusted_device_email', email);
          }

          window.dispatchEvent(new Event('dms-auth-updated'));
          setTimeout(() => { window.location.href = '#/dms/dashboard'; }, 1500);
        }
      } else {
        setStatus('error');
        setMessage(data.message || 'Verification failed.');
      }
    } catch (err) {
      setStatus('error');
      setMessage('Network error during verification.');
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    setCanResend(false);
    setCooldown(60);
    setStatus('idle');
    setMessage('');

    const isLoginIntent = intent === 'login' || intent.startsWith('login');

    try {
      const endpoint    = isLoginIntent ? '/api/auth/send-login-otp' : '/api/auth/resend-verification';
      const bodyPayload = { email };

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyPayload)
      });
      const data = await response.json();
      if (response.ok) {
        setMessage('A new code has been sent to your email.');
      } else {
        setMessage(data.message || 'Failed to resend code.');
      }
    } catch (err) {
      setMessage('Network error resending code.');
    }
  };

  const isLoginIntent = intent === 'login' || intent.startsWith('login');

  return (
    <div className="otp-page-wrapper">
      <div className="otp-card">
        <div className="otp-icon-badge">
          {isLoginIntent ? <ShieldCheck size={28} /> : <Lock size={28} />}
        </div>
        
        <h2 className="otp-title">{isLoginIntent ? 'Login Verification' : 'Email Verification'}</h2>
        <p className="otp-subtitle">
          Enter the 6-digit verification code sent to <br />
          <span className="otp-email-highlight">{email}</span>
        </p>

        {status === 'success' ? (
          <div style={{ textAlign: 'center', animation: 'otpCardScaleUp 0.3s ease-out' }}>
            <div className="otp-status-success" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <CheckCircle size={18} />
              <span>{message}</span>
            </div>
            {!isLoginIntent && (
              <button
                className="otp-submit-btn"
                onClick={() => window.location.href = '#/'}
                style={{ marginTop: '1.25rem' }}
              >
                Go to Login
              </button>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <div className="otp-inputs-group" onPaste={handlePaste}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputsRef.current[index] = el)}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className={`otp-input-field ${digit ? 'filled' : ''}`}
                />
              ))}
            </div>

            {/* Remember this device — only shown for login OTP */}
            {isLoginIntent && (
              <label className="otp-remember-row">
                <input
                  type="checkbox"
                  className="otp-checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                />
                Remember this device for 30 days
              </label>
            )}

            {status === 'error'     && <div className="otp-status-error">{message}</div>}
            {status === 'verifying' && <div className="otp-status-info">Verifying security token…</div>}
            {status === 'idle' && message && <div className="otp-status-info">{message}</div>}

            <button
              type="submit"
              disabled={status === 'verifying'}
              className="otp-submit-btn"
            >
              {status === 'verifying' ? (
                <>
                  <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  Verifying…
                </>
              ) : (
                'Verify Code'
              )}
            </button>
          </form>
        )}

        {status !== 'success' && (
          <div className="otp-footer-links">
            <a href="#/" className="otp-link-back">
              <ArrowLeft size={15} /> Back to Login
            </a>
            <button
              type="button"
              onClick={handleResend}
              disabled={!canResend}
              className="otp-resend-btn"
            >
              {canResend ? 'Resend Code' : `Resend in ${cooldown}s`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default VerifyEmail;
